import { ENABLE_OPTION_FLOW_READ_MODE } from './optionFlowRead';
import { OPTION_FLOW_READ_LABELS, OptionFlowReadLabel } from './getOptionFlowSideIntelligence';

export enum SliderDirection {
  All = 'all',
  Above = 'above',
  Below = 'below',
}

export const MIN_VALUE = 0;
export const MAX_VALUE = 9007199254740991;
export const NEGATIVE_MIN_VALUE = -9007199254740991;

export const MIN_PRICE = 0.5;
export const MAX_PRICE = 50;

export const MIN_OTM = 0;
export const MAX_OTM = 100;

export const MIN_FLOW = 50;
export const MAX_FLOW = 100;

export const MIN_MOMENTUM = 0;
export const MAX_MOMENTUM = 10;

export const MIN_SIZE = 500;
export const MAX_SIZE = 10000;

export const MIN_PREMIUM = 20000;
export const MAX_PREMIUM = 1000000;

export const MIN_VOLATILITY = 25;
export const MAX_VOLATILITY = 100;

export const MIN_OPEN_INTEREST = 0;
export const MAX_OPEN_INTEREST = 75000;

export const MIN_TIME = 0;
export const MAX_TIME = 131500; //3 months in minutes

export const MIN_MARKET_CAP = 300000000;
export const MAX_MARKET_CAP = 200000000000;

export const FLOW_FILTER_STATE_VERSION = 4;
export const DEFAULT_FLOW_SIDE_BASIS = 'ask';
export const DEFAULT_FLOW_SIDE_BASIS_SELECTION = ['aboveAsk', DEFAULT_FLOW_SIDE_BASIS] as const;
export const DEFAULT_BUILT_IN_PRESET_FLOW_SIDE_BASIS = DEFAULT_FLOW_SIDE_BASIS_SELECTION;
export const DEFAULT_PUT_FLOW_BASIS = 'premium';
export const DEFAULT_FLOW_METRIC_MODE = 'total';
export const DEFAULT_FLOW_BIAS = 'All';
export const ENABLE_FLOW_BIAS_FILTER = false;

export type FlowSideBasis = 'all' | 'aboveAsk' | 'ask' | 'mid' | 'bid' | 'belowBid';
export type FlowSideBasisSelection = FlowSideBasis | FlowSideBasis[];
export type PutFlowBasis = 'volume' | 'premium';
export type FlowMetricMode = 'total' | 'net';
export type FlowReadLabelSelection = 'All' | OptionFlowReadLabel;
export type FlowOptionTypeSelection = 'All' | 'Calls' | 'Puts';
export type FlowBiasSelection = 'All' | 'Bullish' | 'Bearish';
export type FlowBasisOverrides = {
  flowSideBasis?: FlowSideBasisSelection;
  putFlowBasis?: PutFlowBasis;
  flowMetricMode?: FlowMetricMode;
};

export const getDefaultFlowSideBasisSelection = (): FlowSideBasis[] => [
  ...DEFAULT_FLOW_SIDE_BASIS_SELECTION,
];

const arraysShallowEqual = <T>(left: readonly T[], right: readonly T[]): boolean =>
  left.length === right.length && left.every((value, index) => value === right[index]);

const preserveArrayIdentity = <T>(source: unknown, computed: readonly T[]): T[] =>
  Array.isArray(source) && arraysShallowEqual(source as readonly T[], computed)
    ? (source as T[])
    : (computed as T[]);

const PUT_FLOW_BASIS_VALUES: PutFlowBasis[] = ['volume', 'premium'];
const FLOW_METRIC_MODE_VALUES: FlowMetricMode[] = ['total', 'net'];
const FLOW_OPTION_TYPE_VALUES: FlowOptionTypeSelection[] = ['All', 'Calls', 'Puts'];
const FLOW_BIAS_VALUES: FlowBiasSelection[] = ['All', 'Bullish', 'Bearish'];
const ABOVE_ASK_TRADE_TYPE = 'Above Ask';
const BELOW_BID_TRADE_TYPE = 'Below Bid';
const LEGACY_ABOVE_ASK_TRADE_TYPE_ALIASES = new Set([
  ABOVE_ASK_TRADE_TYPE.toLowerCase().replace(/[\s_-]/g, ''),
  'aa',
]);
const LEGACY_BELOW_BID_TRADE_TYPE_ALIASES = new Set([
  BELOW_BID_TRADE_TYPE.toLowerCase().replace(/[\s_-]/g, ''),
  'bb',
  'bbid',
]);
const FLOW_READ_LABEL_VALUES: FlowReadLabelSelection[] = ['All', ...OPTION_FLOW_READ_LABELS];

const normalizeTradeTypeLabel = (tradeType: unknown): string => {
  const normalized = String(tradeType || '')
    .trim()
    .toLowerCase()
    .replace(/[\s_-]/g, '');

  if (LEGACY_ABOVE_ASK_TRADE_TYPE_ALIASES.has(normalized)) {
    return ABOVE_ASK_TRADE_TYPE;
  }

  if (LEGACY_BELOW_BID_TRADE_TYPE_ALIASES.has(normalized)) {
    return BELOW_BID_TRADE_TYPE;
  }

  return String(tradeType || '').trim();
};

const getFirstQueryValue = (value: unknown) => (Array.isArray(value) ? value[0] : value);

const getFirstParsedValue = <T>(values: unknown[], parseValue: (value: unknown) => T | undefined) =>
  values.reduce<T | undefined>((result, value) => result || parseValue(value), undefined);

export const parseFlowSideBasis = (value: unknown): FlowSideBasis | undefined => {
  const normalized = String(getFirstQueryValue(value) || '')
    .trim()
    .toLowerCase()
    .replace(/[\s_-]/g, '');

  switch (normalized) {
    case 'all':
      return 'all';
    case 'aboveask':
    case 'aa':
    case 'aask':
      return 'aboveAsk';
    case 'ask':
    case 'a':
      return 'ask';
    case 'mid':
    case 'm':
      return 'mid';
    case 'bid':
    case 'b':
      return 'bid';
    case 'belowbid':
    case 'bb':
    case 'bbid':
      return 'belowBid';
    default:
      return undefined;
  }
};

export const parseFlowSideBasisSelection = (value: unknown): FlowSideBasis[] | undefined => {
  const rawValues = Array.isArray(value) ? value : [value];
  const values = rawValues
    .flatMap((item) => String(item || '').split(','))
    .map((item) => item.trim())
    .filter(Boolean);
  const parsedValues = values
    .map((item) => parseFlowSideBasis(item))
    .filter((item): item is FlowSideBasis => Boolean(item));
  const uniqueValues = Array.from(new Set(parsedValues));

  if (uniqueValues.length === 0) {
    return undefined;
  }

  if (uniqueValues.includes('all')) {
    return ['all'];
  }

  return uniqueValues;
};

export const normalizeFlowSideBasisSelection = (
  value?: FlowSideBasisSelection | null,
): FlowSideBasis[] => parseFlowSideBasisSelection(value) || getDefaultFlowSideBasisSelection();

// The five concrete trade sides in canonical order (bullish → bearish). `'all'` is a
// shorthand for "every one of these".
export const ALL_FLOW_SIDES: FlowSideBasis[] = ['aboveAsk', 'ask', 'mid', 'bid', 'belowBid'];

// Which concrete sides are "on" for a given basis — `['all']` lights up every side.
export const expandFlowSideSelection = (value?: FlowSideBasisSelection | null): FlowSideBasis[] => {
  const normalized = normalizeFlowSideBasisSelection(value);
  return normalized.includes('all')
    ? [...ALL_FLOW_SIDES]
    : ALL_FLOW_SIDES.filter((side) => normalized.includes(side));
};

// Toggle one side within a multi-select basis. Expands `'all'` to the explicit five,
// flips the clicked side, then collapses a full set back to `['all']` and falls back to
// the default selection when the user clears everything (an empty side filter is never
// a valid state).
export const toggleFlowSideSelection = (
  value: FlowSideBasisSelection | null | undefined,
  side: FlowSideBasis,
): FlowSideBasis[] => {
  const active = expandFlowSideSelection(value);
  const next = active.includes(side)
    ? active.filter((current) => current !== side)
    : ALL_FLOW_SIDES.filter((current) => active.includes(current) || current === side);

  if (next.length === 0) {
    return getDefaultFlowSideBasisSelection();
  }

  if (ALL_FLOW_SIDES.every((current) => next.includes(current))) {
    return ['all'];
  }

  return next;
};

export const isDefaultFlowSideBasisSelection = (value?: FlowSideBasisSelection | null): boolean => {
  const flowSideSelection = normalizeFlowSideBasisSelection(value);
  return (
    flowSideSelection.length === DEFAULT_FLOW_SIDE_BASIS_SELECTION.length &&
    DEFAULT_FLOW_SIDE_BASIS_SELECTION.every((side) => flowSideSelection.includes(side))
  );
};

const isLegacyDefaultFlowSideBasisSelection = (value?: FlowSideBasis[] | null): boolean =>
  value?.length === 1 && value[0] === DEFAULT_FLOW_SIDE_BASIS;

const normalizeTradeTypeSelection = (tradeType: unknown): string[] | undefined => {
  if (!Array.isArray(tradeType)) {
    return undefined;
  }

  const normalizedTradeTypes = tradeType.map(normalizeTradeTypeLabel).filter(Boolean);
  return Array.from(new Set(normalizedTradeTypes));
};

export const parsePutFlowBasis = (value: unknown): PutFlowBasis | undefined => {
  const normalized = String(getFirstQueryValue(value) || '').toLowerCase();
  return PUT_FLOW_BASIS_VALUES.includes(normalized as PutFlowBasis)
    ? (normalized as PutFlowBasis)
    : undefined;
};

export const parseFlowMetricMode = (value: unknown): FlowMetricMode | undefined => {
  const normalized = String(getFirstQueryValue(value) || '').toLowerCase();
  return FLOW_METRIC_MODE_VALUES.includes(normalized as FlowMetricMode)
    ? (normalized as FlowMetricMode)
    : undefined;
};

const normalizeCompactLabel = (value: unknown) =>
  String(getFirstQueryValue(value) || '')
    .trim()
    .toLowerCase()
    .replace(/[\s_-]/g, '');

export const parseFlowOptionType = (value: unknown): FlowOptionTypeSelection | undefined => {
  const normalized = normalizeCompactLabel(value);
  const normalizedLabel = String(getFirstQueryValue(value) || '')
    .trim()
    .toLowerCase();

  if (normalized === 'call' || normalized === 'calls' || normalized === 'c') {
    return 'Calls';
  }

  if (normalized === 'put' || normalized === 'puts' || normalized === 'p') {
    return 'Puts';
  }

  if (normalized === 'bullcall' || normalized === 'bullcalls') {
    return 'Calls';
  }

  if (normalized === 'bearcall' || normalized === 'bearcalls') {
    return 'Calls';
  }

  if (normalized === 'bullput' || normalized === 'bullputs') {
    return 'Puts';
  }

  if (normalized === 'bearput' || normalized === 'bearputs') {
    return 'Puts';
  }

  if (normalized === 'bulltrade' || normalized === 'bulltrades') {
    return 'All';
  }

  if (normalized === 'beartrade' || normalized === 'beartrades') {
    return 'All';
  }

  return FLOW_OPTION_TYPE_VALUES.find((optionType) => optionType.toLowerCase() === normalizedLabel);
};

export const parseFlowBias = (value: unknown): FlowBiasSelection | undefined => {
  const normalized = normalizeCompactLabel(value);
  const normalizedLabel = String(getFirstQueryValue(value) || '')
    .trim()
    .toLowerCase();

  if (normalized === 'bull' || normalized === 'bullish') {
    return 'Bullish';
  }

  if (normalized === 'bear' || normalized === 'bearish') {
    return 'Bearish';
  }

  if (
    normalized === 'bullcall' ||
    normalized === 'bullcalls' ||
    normalized === 'bullput' ||
    normalized === 'bullputs' ||
    normalized === 'bulltrade' ||
    normalized === 'bulltrades'
  ) {
    return 'Bullish';
  }

  if (
    normalized === 'bearcall' ||
    normalized === 'bearcalls' ||
    normalized === 'bearput' ||
    normalized === 'bearputs' ||
    normalized === 'beartrade' ||
    normalized === 'beartrades'
  ) {
    return 'Bearish';
  }

  return FLOW_BIAS_VALUES.find((flowBias) => flowBias.toLowerCase() === normalizedLabel);
};

export const hasAskSideSelected = (flowSideBasis?: FlowSideBasisSelection | null): boolean => {
  const flowSideSelection = normalizeFlowSideBasisSelection(flowSideBasis);
  return flowSideSelection.some((side) => side === 'all' || side === 'aboveAsk' || side === 'ask');
};

export const hasBidSideSelected = (flowSideBasis?: FlowSideBasisSelection | null): boolean => {
  const flowSideSelection = normalizeFlowSideBasisSelection(flowSideBasis);
  return flowSideSelection.some((side) => side === 'all' || side === 'bid' || side === 'belowBid');
};

export const hasAskAndBidSidesSelected = (flowSideBasis?: FlowSideBasisSelection | null): boolean =>
  hasAskSideSelected(flowSideBasis) && hasBidSideSelected(flowSideBasis);

export const shouldShowFlowBiasControls = (flowSideBasis?: FlowSideBasisSelection | null) =>
  ENABLE_FLOW_BIAS_FILTER && hasAskSideSelected(flowSideBasis) && hasBidSideSelected(flowSideBasis);

export const getEffectiveFlowOptionType = (
  optionType?: unknown,
  _flowSideBasis?: FlowSideBasisSelection | null,
): FlowOptionTypeSelection => {
  return parseFlowOptionType(optionType) || EQUITY_FILTER.optionType;
};

export const getEffectiveFlowBias = (
  flowBias?: unknown,
  flowSideBasis?: FlowSideBasisSelection | null,
): FlowBiasSelection => {
  if (!shouldShowFlowBiasControls(flowSideBasis)) {
    return DEFAULT_FLOW_BIAS;
  }

  return parseFlowBias(flowBias) || DEFAULT_FLOW_BIAS;
};

// Net mode is a first-class, always-available view: it nets bullish flow (calls on
// the ask, puts on the bid) against bearish flow (puts on the ask, calls on the bid),
// which is meaningful for any trade-side selection — ask only, bid only, or a mix.
// It therefore no longer depends on the selected sides. Total remains the default.
export const getEffectiveFlowMetricMode = (flowMetricMode?: unknown): FlowMetricMode =>
  parseFlowMetricMode(flowMetricMode) || DEFAULT_FLOW_METRIC_MODE;

const parseFlowReadLabel = (value: unknown): FlowReadLabelSelection | undefined => {
  const normalized = String(getFirstQueryValue(value) || '')
    .trim()
    .toLowerCase();

  return FLOW_READ_LABEL_VALUES.find((label) => label.toLowerCase() === normalized);
};

export const normalizeFlowReadLabelSelection = (
  value?: unknown,
  options: { isReadModeEnabled?: boolean } = {},
): FlowReadLabelSelection[] => {
  const isReadModeEnabled = options.isReadModeEnabled ?? ENABLE_OPTION_FLOW_READ_MODE;

  if (!isReadModeEnabled) {
    return ['All'];
  }

  const rawValues = Array.isArray(value) ? value : [value];
  const labels = rawValues
    .flatMap((item) => String(item || '').split(','))
    .map((item) => parseFlowReadLabel(item))
    .filter((item): item is FlowReadLabelSelection => Boolean(item));
  const uniqueLabels = Array.from(new Set(labels));

  if (uniqueLabels.length === 0 || uniqueLabels.includes('All')) {
    return ['All'];
  }

  return uniqueLabels;
};

export const getFlowBasisQueryOverrides = (query: Record<string, unknown>): FlowBasisOverrides => {
  const flowSideBasis = getFirstParsedValue(
    [query.flowSideBasis, query.flowSide, query.side],
    parseFlowSideBasisSelection,
  );
  const putFlowBasis = getFirstParsedValue(
    [query.putFlowBasis, query.flowMetricBasis, query.metricBasis],
    parsePutFlowBasis,
  );
  const flowMetricMode = getFirstParsedValue([query.flowMetricMode], parseFlowMetricMode);

  return {
    ...(flowSideBasis ? { flowSideBasis } : {}),
    ...(putFlowBasis ? { putFlowBasis } : {}),
    ...(flowMetricMode ? { flowMetricMode } : {}),
  };
};

export enum PresetType {
  Equities = 0,
  Unusual = 1,
  Sweeps = 2,
  Momentum = 3,
  ETFs = 4,
  Moonshots = 5,
  Custom = 6,
}

export const isSimilarObject = (referenceObject: any, comparedObject: any) => {
  if (!referenceObject || !comparedObject) {
    return false;
  }

  if (Array.isArray(referenceObject) && Array.isArray(comparedObject)) {
    if (referenceObject.length !== comparedObject.length) {
      return false;
    }

    return referenceObject.every((value, index) => value === comparedObject[index]);
  }

  let isSimilar = true;

  Object.keys(referenceObject).forEach((key) => {
    const referenceValue = referenceObject[key];
    const comparedValue = comparedObject[key];

    if (Array.isArray(referenceValue) && Array.isArray(comparedValue)) {
      if (!isSimilarObject(referenceValue, comparedValue)) {
        // console.log('NOT SIMILAR ARRAY', key, referenceValue, comparedValue);
        isSimilar = false;
      }
    } else if (referenceValue !== comparedValue) {
      // console.log('NOT SIMILAR VALUE', key, referenceValue, comparedValue);
      isSimilar = false;
    }
  });

  return isSimilar;
};

export const EQUITY_FILTER = {
  isPreciseFiltersEnabled: false,
  flowStateVersion: FLOW_FILTER_STATE_VERSION,
  flowSideBasis: getDefaultFlowSideBasisSelection(),
  putFlowBasis: DEFAULT_PUT_FLOW_BASIS as PutFlowBasis,
  flowMetricMode: DEFAULT_FLOW_METRIC_MODE as FlowMetricMode,
  flowThresholdBasis: DEFAULT_PUT_FLOW_BASIS as PutFlowBasis,
  tt: 'All',
  minFlow: MIN_VALUE,
  maxFlow: MAX_VALUE,
  minPrice: MIN_VALUE,
  maxPrice: MAX_VALUE,
  minOtm: NEGATIVE_MIN_VALUE,
  maxOtm: MAX_VALUE,
  minMomentum: MIN_VALUE,
  maxMomentum: MAX_VALUE,
  topPositions: false,
  ultrashort: false,
  short: false,
  medium: false,
  long: false,
  minSize: MIN_VALUE,
  maxSize: MAX_VALUE,
  minPremium: MIN_VALUE,
  maxPremium: MAX_VALUE,
  minImpVolatility: MIN_VALUE,
  maxImpVolatility: MAX_VALUE,
  minOpenInterest: MIN_VALUE,
  maxOpenInterest: MAX_VALUE,
  activeSector: ['All'],
  minTime: MIN_VALUE,
  maxTime: MAX_VALUE,
  minMarketCap: MIN_VALUE,
  maxMarketCap: MAX_VALUE,
  expiration: 'All',
  tradeType: ['All'],
  readLabels: ['All'] as FlowReadLabelSelection[],
  optionType: 'All' as FlowOptionTypeSelection,
  flowBias: DEFAULT_FLOW_BIAS as FlowBiasSelection,
  typedp: ['All'],
  minSizeDp: MIN_VALUE,
  maxSizeDp: MAX_VALUE,
  minAmtDp: MIN_VALUE,
  maxAmtDp: MAX_VALUE,
  minExpiration: MIN_VALUE,
  maxExpiration: MAX_VALUE,
  expirationDirection: SliderDirection.All,
  otmDirection: SliderDirection.All,
  priceDirection: SliderDirection.All,
  flowDirection: SliderDirection.All,
  momentumDirection: SliderDirection.All,
  sizeDirection: SliderDirection.All,
  premiumDirection: SliderDirection.All,
  ivDirection: SliderDirection.All,
  interestDirection: SliderDirection.All,
  marketCapDirection: SliderDirection.All,
  timeDirection: SliderDirection.All,
  sizeDpDirection: SliderDirection.All,
  amtDpDirection: SliderDirection.All,
  selectedExpirations: [] as string[],
};

export type State = typeof EQUITY_FILTER;

export const EQUITY_PRESET_FILTER = {
  ...EQUITY_FILTER,
  flowSideBasis: [...DEFAULT_BUILT_IN_PRESET_FLOW_SIDE_BASIS] as FlowSideBasis[],
};

const LEGACY_EXECUTION_TRADE_TYPES = new Set<string>([ABOVE_ASK_TRADE_TYPE, BELOW_BID_TRADE_TYPE]);

/**
 * "Above Ask" and "Below Bid" used to be aggressive-execution chips in the Flow Type
 * selector that were AND-ed against a broad ask/bid trade-side basis. They're now
 * first-class members of the Trade Side control (`aboveAsk` / `belowBid`), so any
 * saved filter, preset, or direct link that still carries them as trade types is
 * folded into the side selection here and the chip is dropped.
 *
 * The legacy view was `side ∩ aggressiveExecution`; because an above-ask trade is by
 * definition an ask-side trade (and below-bid a bid-side trade), that intersection
 * collapses to just the matching exact side — but only when the saved basis actually
 * included that side. If it didn't (e.g. "Above Ask" paired with a bid-only basis,
 * which historically matched nothing), we keep the original side rather than blanking
 * the view.
 */
const migrateLegacyExecutionTradeTypes = (
  flowSideSelection: FlowSideBasis[] | undefined,
  tradeType: string[] | undefined,
): { flowSideSelection: FlowSideBasis[] | undefined; tradeType: string[] | undefined } => {
  if (!tradeType || tradeType.length === 0) {
    return { flowSideSelection, tradeType };
  }

  const hasLegacyAboveAsk = tradeType.includes(ABOVE_ASK_TRADE_TYPE);
  const hasLegacyBelowBid = tradeType.includes(BELOW_BID_TRADE_TYPE);

  if (!hasLegacyAboveAsk && !hasLegacyBelowBid) {
    return { flowSideSelection, tradeType };
  }

  const baseSelection = flowSideSelection || getDefaultFlowSideBasisSelection();
  const migratedSides: FlowSideBasis[] = [];

  if (hasLegacyAboveAsk && hasAskSideSelected(baseSelection)) {
    migratedSides.push('aboveAsk');
  }

  if (hasLegacyBelowBid && hasBidSideSelected(baseSelection)) {
    migratedSides.push('belowBid');
  }

  return {
    flowSideSelection: migratedSides.length > 0 ? migratedSides : flowSideSelection,
    tradeType: tradeType.filter((type) => !LEGACY_EXECUTION_TRADE_TYPES.has(type)),
  };
};

export const normalizeFlowFilterState = (
  rawState?: Partial<State> | null,
  overrides: FlowBasisOverrides = {},
): State => {
  const source = rawState || {};
  const sourceFlowStateVersion = Number(source.flowStateVersion || 0);
  const shouldMigrateLegacyPutFlowBasis = sourceFlowStateVersion < 3;
  const shouldMigrateLegacyAskSideDefault = sourceFlowStateVersion < 4;
  const sourceFlowThresholdBasis = parsePutFlowBasis(source.flowThresholdBasis);
  const sourcePutFlowBasis = shouldMigrateLegacyPutFlowBasis
    ? DEFAULT_PUT_FLOW_BASIS
    : parsePutFlowBasis(source.putFlowBasis) || DEFAULT_PUT_FLOW_BASIS;
  const putFlowBasis = parsePutFlowBasis(overrides.putFlowBasis) || sourcePutFlowBasis;
  const flowMetricMode =
    parseFlowMetricMode(overrides.flowMetricMode) ||
    parseFlowMetricMode(source.flowMetricMode) ||
    EQUITY_FILTER.flowMetricMode;
  const optionType = parseFlowOptionType(source.optionType) || EQUITY_FILTER.optionType;
  const flowThresholdBasis = sourceFlowThresholdBasis || sourcePutFlowBasis;

  const overrideFlowSideSelection = parseFlowSideBasisSelection(overrides.flowSideBasis);
  const sourceFlowSideSelection = parseFlowSideBasisSelection(source.flowSideBasis);
  const migratedSourceFlowSideSelection =
    !overrideFlowSideSelection &&
    shouldMigrateLegacyAskSideDefault &&
    isLegacyDefaultFlowSideBasisSelection(sourceFlowSideSelection)
      ? getDefaultFlowSideBasisSelection()
      : sourceFlowSideSelection;
  const selectedFlowSideSelection = overrideFlowSideSelection || migratedSourceFlowSideSelection;
  const normalizedTradeType = normalizeTradeTypeSelection(source.tradeType);
  const { flowSideSelection: migratedFlowSideSelection, tradeType: migratedTradeType } =
    migrateLegacyExecutionTradeTypes(selectedFlowSideSelection, normalizedTradeType);
  const computedFlowSideBasis = migratedFlowSideSelection || getDefaultFlowSideBasisSelection();
  const flowSideBasis = preserveArrayIdentity<FlowSideBasis>(
    source.flowSideBasis,
    computedFlowSideBasis,
  );
  const effectiveFlowMetricMode = getEffectiveFlowMetricMode(flowMetricMode);
  const sourceFlowBias = parseFlowBias(source.flowBias);
  const legacyOptionTypeBias = parseFlowBias(source.optionType);
  const flowBias = getEffectiveFlowBias(
    sourceFlowBias && sourceFlowBias !== DEFAULT_FLOW_BIAS
      ? sourceFlowBias
      : legacyOptionTypeBias || sourceFlowBias,
    flowSideBasis,
  );
  const computedTradeType = migratedTradeType
    ? migratedTradeType.filter((item, _, tradeTypes) => item !== 'All' || tradeTypes.length === 1)
    : undefined;
  const resolvedTradeType =
    Array.isArray(computedTradeType) && computedTradeType.length === 0
      ? EQUITY_FILTER.tradeType
      : computedTradeType || EQUITY_FILTER.tradeType;
  const tradeType = preserveArrayIdentity<string>(source.tradeType, resolvedTradeType);
  const computedReadLabels = normalizeFlowReadLabelSelection(source.readLabels);
  const readLabels = preserveArrayIdentity<FlowReadLabelSelection>(
    source.readLabels,
    computedReadLabels,
  );

  const normalizedState: State = {
    ...EQUITY_FILTER,
    ...source,
    ...overrides,
    flowStateVersion: FLOW_FILTER_STATE_VERSION,
    flowSideBasis,
    putFlowBasis,
    flowMetricMode: effectiveFlowMetricMode,
    flowThresholdBasis,
    optionType,
    flowBias,
    tradeType,
    readLabels,
  };

  if (putFlowBasis === 'premium' && sourceFlowThresholdBasis !== 'premium') {
    return {
      ...normalizedState,
      minFlow: EQUITY_FILTER.minFlow,
      maxFlow: EQUITY_FILTER.maxFlow,
      flowDirection: EQUITY_FILTER.flowDirection,
      flowThresholdBasis: 'premium',
    };
  }

  return normalizedState;
};

export const mergeFlowPresetState = (
  currentState: State,
  presetState: Partial<State>,
  overrides: FlowBasisOverrides = {},
): State => {
  return normalizeFlowFilterState(
    {
      ...currentState,
      ...presetState,
      // Built-in presets tune the "unusual activity" criteria only. Ticker type and
      // trade sides are two independent, user-owned dimensions the top nav controls
      // directly, so a preset must never overwrite them — preserve the current values.
      tt: currentState.tt,
      flowSideBasis: currentState.flowSideBasis,
      putFlowBasis: currentState.putFlowBasis,
      flowThresholdBasis: currentState.flowThresholdBasis,
    },
    overrides,
  );
};

/**
 * Does a filter state match a built-in preset's *criteria*, ignoring ticker type and
 * trade sides? Those two dimensions are user-owned and independent of presets (a user
 * can run "Unusual" against Equities-only, ask-side), so preset highlighting keys off
 * the criteria alone. We neutralize `tt`/`flowSideBasis` by copying the preset's own
 * values onto the compared object before the field-by-field similarity check.
 */
export const matchesPresetCriteria = (presetFilter: any, comparedObject: any) => {
  const normalized = normalizeFlowFilterState(comparedObject);
  return isSimilarObject(presetFilter, {
    ...normalized,
    tt: presetFilter.tt,
    flowSideBasis: presetFilter.flowSideBasis,
  });
};

export const isEquityPreset = (comparedObject: any) =>
  matchesPresetCriteria(EQUITY_PRESET_FILTER, comparedObject);

export const UNUSUAL_FILTER = {
  ...EQUITY_FILTER,
  flowSideBasis: [...DEFAULT_BUILT_IN_PRESET_FLOW_SIDE_BASIS] as FlowSideBasis[],
  tradeType: ['Unusual'],
  minFlow: 0,
  maxPrice: MAX_VALUE,
  minOtm: MIN_VALUE,
  minMomentum: MIN_VALUE,
  topPositions: false,
  ultrashort: false,
  short: false,
  medium: false,
  long: false,
};
export const isUnusualPreset = (comparedObject: any) =>
  matchesPresetCriteria(UNUSUAL_FILTER, comparedObject);

export const SWEEP_FILTER = {
  ...EQUITY_FILTER,
  flowSideBasis: [...DEFAULT_BUILT_IN_PRESET_FLOW_SIDE_BASIS] as FlowSideBasis[],
  tradeType: ['Sweeps'],
  minFlow: 0,
  maxPrice: MAX_VALUE,
  /**
   * @note this should come from the original config
   */
  // minOtm: MIN_VALUE,
  minMomentum: MIN_VALUE,
  topPositions: false,
  ultrashort: false,
  short: false,
  medium: false,
  long: false,
};
export const isSweepPreset = (comparedObject: any) =>
  matchesPresetCriteria(SWEEP_FILTER, comparedObject);

export const MOMENTUM_FILTER = {
  ...EQUITY_FILTER,
  flowSideBasis: [...DEFAULT_BUILT_IN_PRESET_FLOW_SIDE_BASIS] as FlowSideBasis[],
  minFlow: 0,
  maxPrice: MAX_VALUE,
  /**
   * @note this should come from the original config
   */
  // minOtm: MIN_VALUE,
  minMomentum: 2,
  topPositions: false,
  ultrashort: false,
  short: false,
  medium: false,
  long: false,
};
export const isMomentumPreset = (comparedObject: any) =>
  matchesPresetCriteria(MOMENTUM_FILTER, comparedObject);

export const ETF_FILTER = {
  ...EQUITY_FILTER,
  flowSideBasis: [...DEFAULT_BUILT_IN_PRESET_FLOW_SIDE_BASIS] as FlowSideBasis[],
  tt: 'ETFs',
  minFlow: 0,
  maxPrice: MAX_VALUE,
  /**
   * @note this should come from the original config
   */
  // minOtm: NEGATIVE_MIN_VALUE,
  minMomentum: MIN_VALUE,
  topPositions: false,
  ultrashort: false,
  short: false,
  medium: false,
  long: false,
};
export const isEtfPreset = (comparedObject: any) =>
  matchesPresetCriteria(ETF_FILTER, comparedObject);

export const MOOSHOT_FILTER = {
  ...EQUITY_FILTER,
  flowSideBasis: [...DEFAULT_BUILT_IN_PRESET_FLOW_SIDE_BASIS] as FlowSideBasis[],
  maxFlow: 20, // This is now put flow, so 95% or more calls
  maxPrice: 3,
  minOtm: 10,
  minMomentum: MIN_VALUE,
  topPositions: false,
  ultrashort: true,
  short: true,
  medium: true,
  long: false,
};
export const isMoonshotPreset = (comparedObject: any) =>
  matchesPresetCriteria(MOOSHOT_FILTER, comparedObject);

export const presetObjects = {
  [PresetType.Equities]: EQUITY_PRESET_FILTER,
  [PresetType.Unusual]: UNUSUAL_FILTER,
  [PresetType.Sweeps]: SWEEP_FILTER,
  [PresetType.Momentum]: MOMENTUM_FILTER,
  [PresetType.ETFs]: ETF_FILTER,
  [PresetType.Moonshots]: MOOSHOT_FILTER,
  [PresetType.Custom]: null,
};

export const presetAggregateSortKey = {
  [PresetType.Equities]: 'otm',
  [PresetType.Unusual]: 'unusualOtm',
  [PresetType.Sweeps]: 'sweepContracts',
  [PresetType.Momentum]: 'momentum',
  [PresetType.ETFs]: 'contracts',
  [PresetType.Moonshots]: 'contracts',
  [PresetType.Custom]: null,
};

export const presetGraphIndex = {
  [PresetType.Equities]: 0,
  [PresetType.Unusual]: 1,
  [PresetType.Sweeps]: 9,
  [PresetType.Momentum]: 2,
  [PresetType.ETFs]: 5,
  [PresetType.Moonshots]: 5,
  [PresetType.Custom]: null,
};
